import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { JEZICI } from '../i18n'
import { DNO_EKRANA, VRH_EKRANA } from './ljuska'
import { MJERA_KRUPNO, MJERA_POLJA, MJERA_VISEREDNO, nativnoPolje } from './polje'

/**
 * "Luxury noir" komplet (CLAUDE.md §17, docs/shuttly-mockup.html) — sučelje
 * gosta i sučelje vozača.
 *
 * Zašto dva kompleta u istoj mapi: `Layoutovi`/`Field`/`Modal` su svijetla
 * varijanta konzole (paper/card/ink), a gost i vozač gledaju tamnu podlogu i
 * zlatnu nit. Prepisivanje boja po komponenti završilo bi kao gomila iznimaka u
 * JSX-u, a §17 traži suprotno — boje dolaze iz tokena i nigdje se ne
 * hardkodiraju. Dodirna meta je najmanje 44px (`touch-target`); oba su sučelja
 * mobile-first i rade se palcem, često u pokretu.
 */

/**
 * Okvir ekrana: uska kolona na sredini, tamna podloga do ruba zaslona.
 *
 * Rubovi nose sigurnu zonu (Z15-polir §2 i §3): uz `viewport-fit=cover` sadržaj
 * klizi pod traku stanja gore i pod traku geste dolje, a ovdje nema ni zaglavlja
 * ni pojasa koji bi ih pokrili. `dvh`, ne `vh` — na iOS-u je `vh` visina zaslona
 * bez traka preglednika, pa je ekran uvijek nešto viši od vidljivog.
 */
export function Ekran({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-noir">
      <div
        className="mx-auto w-full max-w-sm px-5"
        style={{ paddingTop: VRH_EKRANA, paddingBottom: DNO_EKRANA }}
      >
        {children}
      </div>
    </div>
  )
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-center text-[10px] tracking-[0.25em] text-brass uppercase">{children}</p>
  )
}

export function Naslov({
  children,
  lijevo = false,
}: {
  children: ReactNode
  lijevo?: boolean
}) {
  return (
    <h1 className={`text-[23px] text-ivory ${lijevo ? 'text-left' : 'text-center'}`}>{children}</h1>
  )
}

export function Podnaslov({
  children,
  lijevo = false,
}: {
  children: ReactNode
  lijevo?: boolean
}) {
  return (
    <p className={`mt-0.5 mb-5 text-sm text-stone ${lijevo ? 'text-left' : 'text-center'}`}>
      {children}
    </p>
  )
}

/**
 * Povratak na prethodni ekran.
 *
 * `oznaka` imenuje ODREDIŠTE umjesto općeg "Natrag". Kod gosta općenito je
 * dovoljno — na jedan ekran dolazi s više mjesta. Vozač ide iz naloga u vozila i
 * natrag, i to palcem, u vozilu: "← Otvoreni nalozi" mu kaže gdje završava
 * prije nego što tapne.
 */
export function Natrag({ na, oznaka }: { na: string; oznaka?: string }) {
  const { t } = useTranslation()

  return (
    <Link
      to={na}
      className="touch-target mb-2 inline-flex items-center text-sm text-stone hover:text-champagne"
    >
      ← {oznaka ?? t('common.back')}
    </Link>
  )
}

interface KarticaProps {
  zlatna?: boolean
  /**
   * Kartica popisa kroz koji se PRELISTAVA, a ne čita (popis vozila, krug 10,
   * nalaz 6): manji unutarnji zrak, pa ih na zaslon stane više. Razmak i
   * zaobljenje ostaju — zbijena kartica nije kartica bez okvira.
   */
  zbijena?: boolean
  className?: string
  children: ReactNode
}

export function Kartica({ zlatna = false, zbijena = false, className = '', children }: KarticaProps) {
  return (
    <div
      className={`rounded-2xl border bg-surface ${zbijena ? 'mb-2 p-3' : 'mb-3 p-4'} ${
        zlatna ? 'border-brass' : 'border-line'
      } ${className}`}
    >
      {children}
    </div>
  )
}

/** Kartica koja je i radnja (mockup: aktivna narudžba vodi na praćenje). */
export function KarticaLink({
  na,
  zlatna = false,
  children,
}: {
  na: string
  zlatna?: boolean
  children: ReactNode
}) {
  return (
    <Link
      to={na}
      className={`mb-3 block rounded-2xl border bg-surface p-4 transition-colors hover:border-champagne ${
        zlatna ? 'border-brass' : 'border-line'
      }`}
    >
      {children}
    </Link>
  )
}

export function NaslovKartice({ children }: { children: ReactNode }) {
  return <h2 className="text-[17px] text-ivory">{children}</h2>
}

export function OpisKartice({ children }: { children: ReactNode }) {
  return <p className="mt-0.5 mb-2 text-[13px] text-stone">{children}</p>
}

/** Redak podatka: naziv lijevo (mist), vrijednost desno (champagne). */
export function Redak({
  lijevo,
  desno,
  prvi = false,
}: {
  lijevo: ReactNode
  desno: ReactNode
  prvi?: boolean
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-2 text-[13px] ${
        prvi ? '' : 'border-t border-line'
      }`}
    >
      {/*
        LABELA DRŽI ŠIRINU, VRIJEDNOST SE PRELAMA (HITNO2 §3).

        Ovaj redak je jedino mjesto na kojem gost čita PUN opis svoje sobe —
        zaglavlje ljuske ga kraći, jer uz sebe nosi i datume. Dug naziv tipa
        ("Junior suite s pogledom na more i terasom") ovdje se zato ne smije ni
        odrezati ni stisnuti labelu u okomit niz slova: labela ostaje cijela
        (`shrink-0`), a vrijednost prelama, i to i kad je jedna riječ duža od
        stupca (`anywhere`).
      */}
      <span className="shrink-0 text-mist">{lijevo}</span>
      <span className="min-w-0 text-right text-champagne [overflow-wrap:anywhere]">{desno}</span>
    </div>
  )
}

export type PilulaTon = 'gratis' | 'naplatno' | 'tijek' | 'mirno' | 'istaknut' | 'nepovoljno'

const PILULE: Record<PilulaTon, string> = {
  gratis: 'bg-ok-tx/25 text-ok-lite',
  naplatno: 'bg-brass/20 text-champagne',
  tijek: 'bg-warn-tx/30 text-warn-lite',
  mirno: 'bg-line/60 text-stone',

  // Jedini ton koji je PUNA ploha, a ne prigušena: nosi stanje na koje gost
  // mora reagirati sada ("vozilo je stiglo", krug 19). Da izgleda kao ostali,
  // ne bi bio isticanje nego još jedna nijansa iste liste.
  istaknut: 'bg-brass text-noir font-semibold',

  // Jedini ton koji znači "nešto je pošlo po zlu" (dotjerivanje-5 §3). Nije
  // uzbuna kao `istaknut` — od gosta se ništa ne traži odmah — nego iskrenost:
  // propušten zahtjev se ne smije čitati istom bojom kao uredno završen posao.
  nepovoljno: 'bg-bad-tx/30 text-bad-lite',
}

export function Pilula({ ton = 'mirno', children }: { ton?: PilulaTon; children: ReactNode }) {
  return (
    <span className={`inline-block rounded-xl px-2.5 py-1 text-[11px] whitespace-nowrap ${PILULE[ton]}`}>
      {children}
    </span>
  )
}

type Izgled = 'zlatni' | 'obrub' | 'prigusen'

const IZGLEDI: Record<Izgled, string> = {
  zlatni: 'bg-brass text-noir font-semibold hover:bg-champagne',
  obrub: 'bg-transparent text-champagne border border-brass hover:bg-brass/12',
  prigusen: 'bg-transparent text-stone border border-line hover:border-brass hover:text-ivory',
}

interface GumbProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  izgled?: Izgled
  puni?: boolean
  children: ReactNode
}

/**
 * GUMB KOJI NE ŠALJE OBRAZAC DOK MU SE NE KAŽE (D31, 25.08.2026.).
 *
 * Isti račun koji `components/Button.tsx` već drži za konzolu, sad i za noir:
 * nativni `<button>` unutar `<form>`-a je `type="submit"`, pa je svaki `Gumb`
 * pod `Obrazac`om tiho slao obrazac. Trinaest gostovih obrazaca to je preživjelo
 * zato što svaki od njih na SVOM "Pošalji" piše `type="submit"` izrijekom — ali
 * obrana koja se piše na svakom pozivu jednom se zaboravi, i zaboravila se:
 * `Greska` s gumbom "Pokušaj ponovno" (dolje) stoji USRED obrasca narudžbe, pa
 * gostu kojem je osvježavanje popisa stanica palo — a relaciju je već izabrao —
 * taj gumb nije ponavljao dohvat nego NARUČIVAO VOŽNJU.
 *
 * Zato je zadano `button`, a slanje je IZRIČITO. Novi `Gumb` u obrascu je od
 * sada bezopasan dok se ne izjasni (CLAUDE.md §16a).
 */
export function Gumb({
  izgled = 'zlatni',
  puni = true,
  type = 'button',
  className = '',
  children,
  ...ostalo
}: GumbProps) {
  return (
    <button
      type={type}
      className={[
        'touch-target inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        puni ? 'w-full' : '',
        IZGLEDI[izgled],
        className,
      ].join(' ')}
      {...ostalo}
    >
      {children}
    </button>
  )
}

/** Isti izgled kao gumb, ali je stvarno navigacija — čitač zaslona to razlikuje. */
export function GumbLink({
  na,
  izgled = 'zlatni',
  children,
}: {
  na: string
  izgled?: Izgled
  children: ReactNode
}) {
  return (
    <Link
      to={na}
      className={[
        'touch-target flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm',
        'transition-colors',
        IZGLEDI[izgled],
      ].join(' ')}
    >
      {children}
    </Link>
  )
}

/**
 * KRUPNE ODLUKE (krug 10, nalaz 5).
 *
 * Polje kroz koje korisnik nešto BIRA — stanicu, termin, vrstu, sobu — nosi
 * `krupno` i time dobiva veću metu, veće slovo i deblji obrub. Ostala polja
 * (napomena, marka auta, boja) ostaju kakva jesu: kad je sve krupno, ništa nije
 * istaknuto, a obrazac naraste toliko da odluka i potvrda više ne stanu na isti
 * zaslon.
 *
 * Razlika nije samo estetska. Odabir se radi palcem, u pokretu, često na suncu i
 * s jednom rukom na koferu; a slovo manje od 16 px iOS pri fokusu ZUMIRA, pa se
 * ekran nakon svakog dodira sam pomakne. Zato je krupno polje 17 px — iznad te
 * granice, bez ijedne iznimke po komponenti (§17: boje i mjere iz jednog mjesta).
 */
/**
 * Boja je noirova; MJERA dolazi iz zajedničkog izvora (`components/polje.ts`) —
 * ista ona koju čita konzola (D15-A §3). Dva kompleta smiju imati dvije palete,
 * ali ne i dvije visine: polje koje je na jednom sučelju 44 px, a na drugom 46,
 * razlika je koja se primijeti tek kad dvije kontrole stoje jedna uz drugu.
 */
const TON_POLJA = 'bg-noir text-ivory placeholder:text-stone/70 focus:outline-none disabled:opacity-60'

const POLJE = `${MJERA_POLJA} ${TON_POLJA}`

const POLJE_KRUPNO = `${MJERA_KRUPNO} ${TON_POLJA}`

interface OkvirProps {
  oznaka: string
  hint?: string
  greska?: string
  krupno?: boolean
  children: (id: string, opisano: string | undefined) => ReactNode
}

/**
 * Natpis, podsjetnik i poruka oko kontrole — noir izdanje. Izvezeno je zbog
 * vlastitog pickera (`components/datum/`), koji ista polja crta na OBA sučelja:
 * mjere i ponašanje su mu isti, a okvir mora biti onaj kojeg ekran već nosi.
 */
export function Okvir({ oznaka, hint, greska, krupno = false, children }: OkvirProps) {
  const id = useId()
  const idHint = hint ? `${id}-hint` : undefined
  const idGreska = greska ? `${id}-greska` : undefined
  const opisano = [idHint, idGreska].filter(Boolean).join(' ') || undefined

  return (
    <div className={`flex flex-col gap-1.5 ${krupno ? 'mb-4' : 'mb-3'}`}>
      <label
        htmlFor={id}
        className={`tracking-[0.15em] text-brass uppercase ${krupno ? 'text-[12px]' : 'text-[11px]'}`}
      >
        {oznaka}
      </label>

      {children(id, opisano)}

      {hint ? (
        <p id={idHint} className="text-xs text-stone">
          {hint}
        </p>
      ) : null}

      {greska ? (
        <p id={idGreska} role="alert" className="text-xs text-champagne">
          {greska}
        </p>
      ) : null}
    </div>
  )
}

interface UnosProps extends InputHTMLAttributes<HTMLInputElement> {
  oznaka: string
  hint?: string
  greska?: string
  /** Polje kojim se nešto BIRA (soba, termin) — veća meta i veće slovo. */
  krupno?: boolean
}

export function Unos({ oznaka, hint, greska, krupno = false, ...ostalo }: UnosProps) {
  // Datum i vrijeme traže vlastiti popravak (`components/polje.ts`): 24-satni
  // brojčanik i širina koja poštuje rub kolone.
  const nativno = nativnoPolje(ostalo.type)

  return (
    <Okvir oznaka={oznaka} hint={hint} greska={greska} krupno={krupno}>
      {(id, opisano) => (
        <input
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          lang={nativno.lang}
          className={`${krupno ? POLJE_KRUPNO : POLJE} ${nativno.razred} ${
            greska ? 'border-champagne' : 'border-line focus:border-brass'
          }`}
          {...ostalo}
        />
      )}
    </Okvir>
  )
}

interface OdabirProps extends SelectHTMLAttributes<HTMLSelectElement> {
  oznaka: string
  hint?: string
  greska?: string
  /** Izbor je odluka, a ne polje obrasca — stanica, termin, vrsta, auto. */
  krupno?: boolean
  children: ReactNode
}

export function Odabir({ oznaka, hint, greska, krupno = false, children, ...ostalo }: OdabirProps) {
  return (
    <Okvir oznaka={oznaka} hint={hint} greska={greska} krupno={krupno}>
      {(id, opisano) => (
        <select
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          className={`${krupno ? POLJE_KRUPNO : POLJE} ${
            greska ? 'border-champagne' : 'border-line focus:border-brass'
          }`}
          {...ostalo}
        >
          {children}
        </select>
      )}
    </Okvir>
  )
}

interface ViserednoProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  oznaka: string
  hint?: string
  greska?: string
}

/**
 * VIŠEREDNO POLJE noir kompleta (D15-A §3).
 *
 * Gost i vozač ga danas nemaju nijedno — i upravo zato stoji ovdje: zakon
 * (`components/zakonPolja.test.ts`) traži da polje dolazi iz kompleta, pa prvi
 * ekran kojemu zatreba odlomak ne smije birati između golog `<textarea>`-a i
 * čekanja. Boje, natpis i poruka uz polje su isti kao kod `Unos`a.
 */
export function Viseredno({ oznaka, hint, greska, ...ostalo }: ViserednoProps) {
  return (
    <Okvir oznaka={oznaka} hint={hint} greska={greska}>
      {(id, opisano) => (
        <textarea
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          className={`${MJERA_VISEREDNO} ${TON_POLJA} ${
            greska ? 'border-champagne' : 'border-line focus:border-brass'
          }`}
          {...ostalo}
        />
      )}
    </Okvir>
  )
}

/**
 * BROJ OSOBA KAO STEPPER: − [broj] + (dotjerivanje-3 §2).
 *
 * **Brojka se ne tipka.** `<input type="number">` na mobitelu otvara tipkovnicu
 * zbog jedne znamenke, nudi strelice veličine dva piksela i pušta da se polje
 * isprazni usred upisa — pa je "za koliko vas" bilo jedino mjesto u obrascu na
 * kojem se moglo zapeti. Dva gumba i broj između njih odgovaraju na to pitanje u
 * jednom dodiru, i to palcem.
 *
 * **Granice drži KOMPONENTA, ne korisnik**: na rubu se gumb gasi umjesto da
 * primi tap koji ništa ne radi. Vrijednost izvan raspona (starije stanje, tuđi
 * zapis) se ne ispravlja tiho — prikazuje se kakva jest, a gumbi je vraćaju
 * unutra: tiha ispravka je izmjena koju nitko nije zatražio.
 *
 * Visina je ista kao kod ostalih krupnih polja (`min-h-14`), jer stoji u istom
 * stupcu: obrazac u kojem svako polje ima svoju visinu nije obrazac nego niz.
 */
export function Brojac({
  oznaka,
  hint,
  vrijednost,
  naPromjenu,
  najmanje = 1,
  najvise = 99,
  manjeOznaka,
  viseOznaka,
}: {
  oznaka: string
  hint?: string
  vrijednost: number
  naPromjenu: (vrijednost: number) => void
  najmanje?: number
  najvise?: number
  /** Imena gumba za čitač zaslona; i18n ostaje na pozivatelju. */
  manjeOznaka: string
  viseOznaka: string
}) {
  const razred =
    'grid size-14 shrink-0 place-items-center rounded-xl border-2 border-line text-[22px] ' +
    'text-ivory disabled:opacity-30'

  return (
    <Okvir oznaka={oznaka} hint={hint} krupno>
      {(id, opisano) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-brojac="manje"
            aria-label={manjeOznaka}
            disabled={vrijednost <= najmanje}
            onClick={() => naPromjenu(Math.max(najmanje, vrijednost - 1))}
            className={razred}
          >
            −
          </button>

          {/*
            Brojka je izlaz, ne polje: `role="status"` je čita kad se promijeni, a
            `id` je veže uz oznaku iznad — pa čitač zaslona i dalje zna ČEGA je to
            broj. Rasteže se preko ostatka retka, da polje bude pune širine kao i
            sva ostala u obrascu.
          */}
          <output
            id={id}
            role="status"
            aria-describedby={opisano}
            data-brojac="vrijednost"
            className="flex min-h-14 flex-1 items-center justify-center rounded-xl border-2 border-line bg-noir text-[17px] text-ivory"
          >
            {vrijednost}
          </output>

          <button
            type="button"
            data-brojac="vise"
            aria-label={viseOznaka}
            disabled={vrijednost >= najvise}
            onClick={() => naPromjenu(Math.min(najvise, vrijednost + 1))}
            className={razred}
          >
            +
          </button>
        </div>
      )}
    </Okvir>
  )
}

/** Cijena je uvijek serverska (CLAUDE.md §7) — ovaj okvir je prikaz, ne račun. */
export function CijenaOkvir({
  oznaka,
  iznos,
  napomena,
  dodatno,
}: {
  oznaka: string
  iznos: string
  napomena?: string
  dodatno?: ReactNode
}) {
  return (
    <div className="mb-4 rounded-xl border border-brass bg-surface px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-mist">{oznaka}</span>
        <span className="font-serif text-xl text-champagne">{iznos}</span>
      </div>
      {napomena ? <p className="mt-1 text-xs text-stone">{napomena}</p> : null}
      {dodatno ? <div className="mt-1 text-xs text-stone">{dodatno}</div> : null}
    </div>
  )
}

export type StanjeKoraka = 'gotov' | 'sada' | 'ceka'

/** Stavka timelinea (mockup g-status): kružić na niti + naslov + vrijeme. */
export function Korak({
  stanje,
  naslov,
  opis,
  zadnji = false,
}: {
  stanje: StanjeKoraka
  naslov: string
  opis?: string | null
  zadnji?: boolean
}) {
  const oznake: Record<StanjeKoraka, string> = {
    gotov: 'border-brass text-champagne',
    sada: 'border-champagne text-champagne shadow-[0_0_0_3px_var(--akcent-prsten)]',
    ceka: 'border-line text-stone',
  }

  const znak = stanje === 'gotov' ? '✓' : stanje === 'sada' ? '●' : '○'

  return (
    <div
      className={`relative ml-2.5 flex gap-3 py-2 pl-4 ${
        zadnji ? 'border-l border-transparent' : 'border-l border-line'
      }`}
    >
      <i
        aria-hidden="true"
        className={`absolute -left-2.5 top-2 flex size-5 items-center justify-center rounded-full border bg-noir text-[10px] not-italic ${oznake[stanje]}`}
      >
        {znak}
      </i>
      <div>
        <b className={`block text-sm font-semibold ${stanje === 'ceka' ? 'text-stone' : 'text-ivory'}`}>
          {naslov}
        </b>
        <small className="text-xs text-stone">{opis ?? '—'}</small>
      </div>
    </div>
  )
}

export function Uputa({ children }: { children: ReactNode }) {
  return <p className="mt-3 text-center text-xs text-stone">{children}</p>
}

/** Greška koja je već prevedena (i18n ključ iz `title`) — ovdje samo prikaz. */
/**
 * Greška s IZLAZOM (Z22c §E).
 *
 * `ponovi` je izlaz za ono što je palo slučajno (mreža, istek); `izlaz` je izlaz
 * za ono što je palo iz razloga koji korisnik može ukloniti — a to je u praksi
 * jedno: kriva ili nikakva sesija. Rečenica bez gumba je zid, pogotovo gostu
 * koji lozinku nema ni nema gdje dobiti.
 *
 * Izlaz se NE računa ovdje nego u `i18n/greske.ts` (`izlazGreske`), uz poruku uz
 * koju stoji: dva računa značila bi rečenicu "prijavite se" bez gumba.
 */
export function Greska({
  poruka,
  ponovi,
  izlaz,
}: {
  poruka: string
  ponovi?: () => void
  izlaz?: { tekst: string; na: string } | null
}) {
  const { t } = useTranslation()

  return (
    <div role="alert" className="mb-3 rounded-xl border border-champagne/40 bg-surface p-4">
      <p className="text-sm text-champagne">{poruka}</p>

      {izlaz ? (
        <Link
          to={izlaz.na}
          data-izlaz="greske"
          className="mt-3 inline-flex min-h-11 items-center rounded-xl border border-brass bg-brass/12 px-4 text-[15px] text-champagne"
        >
          {izlaz.tekst}
        </Link>
      ) : null}

      {ponovi ? (
        <Gumb izgled="prigusen" className="mt-3" onClick={ponovi}>
          {t('common.retry')}
        </Gumb>
      ) : null}
    </div>
  )
}

export function Ucitavanje() {
  const { t } = useTranslation()

  return (
    <div role="status" className="flex items-center justify-center gap-3 py-10 text-sm text-stone">
      <span className="size-4 animate-spin rounded-full border-2 border-line border-t-brass" />
      {t('state.loading')}
    </div>
  )
}

export function Praznina({ naslov, opis }: { naslov: string; opis: string }) {
  return (
    <Kartica className="text-center">
      <NaslovKartice>{naslov}</NaslovKartice>
      <p className="mt-2 text-[13px] text-stone">{opis}</p>
    </Kartica>
  )
}

/**
 * Odabir jezika (hr/en/de) u tamnoj varijanti — jedina postavka koju gost i
 * vozač imaju na ekranu. Odabrani jezik je uz refresh token jedino što smije u
 * `localStorage` (CLAUDE.md §13).
 */
export function IzbornikJezika() {
  const { t, i18n } = useTranslation()

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">{t('lang.label')}</span>
      <select
        value={i18n.resolvedLanguage}
        onChange={(dogadaj) => void i18n.changeLanguage(dogadaj.target.value)}
        className="touch-target rounded-full border border-line bg-transparent px-3 py-1.5 text-xs text-stone hover:border-brass"
      >
        {JEZICI.map((jezik) => (
          <option key={jezik} value={jezik} className="text-ink">
            {t(`lang.${jezik}`)}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * Kratka potvrda radnje (mockup: `flash`) — mirna, bez modala.
 *
 * `role="status"` jer se javlja SAMA (promjena statusa vožnje, gubitak veze), a
 * ne kao odgovor na tap: čitač ekrana je inače ne bi pročitao. Nije `alert` —
 * vijest ne prekida ono što gost upravo radi.
 */
export function Obavijest({ poruka }: { poruka: string }) {
  return (
    <div
      role="status"
      className="mb-3 rounded-xl border border-brass/50 bg-brass/10 px-4 py-3 text-[13px] text-champagne"
    >
      {poruka}
    </div>
  )
}
